import { useCallback, useEffect, useRef, useState } from 'react';
import { adminCommentsApi, clientApi } from './api';
import type { CommentThread, CreateThreadRequest, AddReplyRequest } from '../../shared/types';

const POLL_INTERVAL_MS = 15_000;

export interface UseScriptCommentsArgs {
  mode: 'admin' | 'client';
  scriptId?: string; // required when mode === 'admin'
  slug?: string;     // required when mode === 'client'
  enabled: boolean;
}

export interface UseScriptCommentsResult {
  threads: CommentThread[];
  loading: boolean;
  /** Latest doc_version reported by the server (client mode only). The
   *  review page compares this to the version it loaded to show a
   *  "script was updated — refresh" cue on stale open tabs. */
  scriptDocVersion: number | null;
  /** Round the client page is on now (2A), from the same poll. */
  scriptRound: number | null;
  refresh: () => void;
  createThread: (body: CreateThreadRequest) => Promise<void>;
  reply: (threadId: string, body: AddReplyRequest) => Promise<void>;
  resolve: (threadId: string) => Promise<void>;
  reopen: (threadId: string) => Promise<void>;
}

export function useScriptComments(args: UseScriptCommentsArgs): UseScriptCommentsResult {
  const { mode, scriptId, slug, enabled } = args;
  const [threads, setThreads] = useState<CommentThread[]>([]);
  const [scriptDocVersion, setScriptDocVersion] = useState<number | null>(null);
  const [scriptRound, setScriptRound] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchingRef = useRef(false);

  const fetchThreads = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      let res: { threads: CommentThread[]; script_doc_version?: number; published_round?: number | null };
      if (mode === 'admin') {
        if (!scriptId) return;
        res = await adminCommentsApi.list(scriptId);
      } else {
        if (!slug) return;
        res = await clientApi.listComments(slug);
      }
      setThreads(res.threads);
      if (typeof res.script_doc_version === 'number') {
        setScriptDocVersion(res.script_doc_version);
      }
      if (typeof res.published_round === 'number') setScriptRound(res.published_round);
    } catch {
      // Soft-fail: keep last-known state.
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [mode, scriptId, slug]);

  useEffect(() => {
    if (!enabled) return;
    void fetchThreads();
    let timer: ReturnType<typeof setInterval> | null = null;

    function startPoll() {
      stopPoll();
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') void fetchThreads();
      }, POLL_INTERVAL_MS);
    }
    function stopPoll() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') void fetchThreads();
    }

    startPoll();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopPoll();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, fetchThreads]);

  const createThread = useCallback(
    async (body: CreateThreadRequest) => {
      if (mode === 'admin' && scriptId) {
        await adminCommentsApi.createThread(scriptId, body);
      } else if (mode === 'client' && slug) {
        await clientApi.createThread(slug, body);
      }
      await fetchThreads();
    },
    [mode, scriptId, slug, fetchThreads],
  );

  const reply = useCallback(
    async (threadId: string, body: AddReplyRequest) => {
      if (mode === 'admin' && scriptId) {
        await adminCommentsApi.reply(scriptId, threadId, body);
      } else if (mode === 'client' && slug) {
        await clientApi.reply(slug, threadId, body);
      }
      await fetchThreads();
    },
    [mode, scriptId, slug, fetchThreads],
  );

  const resolve = useCallback(
    async (threadId: string) => {
      if (mode === 'admin' && scriptId) {
        await adminCommentsApi.resolve(scriptId, threadId);
      } else if (mode === 'client' && slug) {
        await clientApi.resolveComment(slug, threadId);
      }
      await fetchThreads();
    },
    [mode, scriptId, slug, fetchThreads],
  );

  const reopen = useCallback(
    async (threadId: string) => {
      if (mode === 'admin' && scriptId) {
        await adminCommentsApi.reopen(scriptId, threadId);
      } else if (mode === 'client' && slug) {
        await clientApi.reopenComment(slug, threadId);
      }
      await fetchThreads();
    },
    [mode, scriptId, slug, fetchThreads],
  );

  return { threads, loading, scriptDocVersion, scriptRound, refresh: fetchThreads, createThread, reply, resolve, reopen };
}
